import { Link } from '@tanstack/react-router';
import { CheckCircle2, ChevronDown, ChevronRight, RotateCcw } from 'lucide-react';
import { useState } from 'react';

import { AdminLayout } from '@/app/shell/admin-layout';
import {
  type AdminErrorGroup,
  type ErrorSource,
  type ErrorState,
  type ErrorWindow,
  type ListErrorsQuery,
  useAdminErrors,
  useResolveError,
} from '@/shared/api/admin-errors';
import { cn } from '@/shared/lib/cn';
import { ErrorBanner, PageHeader, Pagination, TableShell, TableStateRow, THEAD, WIDE } from '@/shared/ui/kit';

const PAGE_SIZE = 50;

/**
 * Self-hosted error monitoring — grouped web crashes (POST /api/client-errors)
 * and API 5xx (GlobalExceptionFilter), one row per fingerprint. Groups with no
 * occurrence for 30 days are purged; a resolved group reopens on recurrence.
 */
export function ErrorsPage() {
  const [source, setSource] = useState<ErrorSource | undefined>(undefined);
  const [win, setWin] = useState<ErrorWindow>('24h');
  const [state, setState] = useState<ErrorState>('open');
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);

  const query: ListErrorsQuery = { window: win, state, limit: PAGE_SIZE, offset };
  if (source) query.source = source;
  const { data, isFetching, error } = useAdminErrors(query);
  const resolve = useResolveError();

  const reset = () => setOffset(0);

  return (
    <AdminLayout>
      <PageHeader
        title="Errors"
        description="Web crashes and API 5xx, grouped by fingerprint (message + top frame). Count is total since first seen; a resolved group reopens if it happens again. Groups silent for 30 days are deleted."
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Segmented
          label="Source"
          value={source ?? 'all'}
          options={['all', 'web', 'api']}
          onChange={(v) => {
            setSource(v === 'all' ? undefined : (v as ErrorSource));
            reset();
          }}
        />
        <Segmented
          label="Last seen"
          value={win}
          options={['24h', '7d', '30d']}
          onChange={(v) => {
            setWin(v as ErrorWindow);
            reset();
          }}
        />
        <Segmented
          label="State"
          value={state}
          options={['open', 'resolved', 'all']}
          onChange={(v) => {
            setState(v as ErrorState);
            reset();
          }}
        />
        <div className="ml-auto text-xs text-neutral-500">
          {isFetching
            ? 'Loading…'
            : data
              ? `${data.total} group(s) · web ${data.summary.web} · api ${data.summary.api} occurrences`
              : null}
        </div>
      </div>

      {error || resolve.error ? <ErrorBanner error={error ?? resolve.error} /> : null}

      <TableShell>
          <thead className={THEAD}>
            <tr>
              <th className="w-8 px-2 py-2" aria-label="Expand"></th>
              <th className="w-16 px-2 py-2 font-medium">Source</th>
              <th className="px-2 py-2 font-medium">Error</th>
              <th className="w-20 px-2 py-2 text-right font-medium">Count</th>
              <th className={`w-32 px-2 py-2 font-medium ${WIDE}`}>First seen</th>
              <th className={`w-32 px-2 py-2 font-medium ${WIDE}`}>Last seen</th>
              <th className="w-28 px-2 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-800">
            {data?.items.map((g) => (
              <ErrorRow
                key={g.id}
                group={g}
                open={expanded === g.id}
                onToggle={() => setExpanded(expanded === g.id ? null : g.id)}
                busy={resolve.isPending && resolve.variables?.id === g.id}
                onResolve={(resolved) => resolve.mutate({ id: g.id, resolved })}
              />
            ))}
            <TableStateRow
              colSpan={7}
              loading={!data && !error}
              empty={Boolean(data && data.items.length === 0)}
              emptyText="No errors in this window."
            />
          </tbody>
      </TableShell>

      {data ? (
        <Pagination total={data.total} offset={offset} pageSize={PAGE_SIZE} onChange={setOffset} />
      ) : null}
    </AdminLayout>
  );
}

function ErrorRow({
  group: g,
  open,
  onToggle,
  busy,
  onResolve,
}: {
  group: AdminErrorGroup;
  open: boolean;
  onToggle: () => void;
  busy: boolean;
  onResolve: (resolved: boolean) => void;
}) {
  return (
    <>
      <tr className="cursor-pointer align-top hover:bg-neutral-800/40" onClick={onToggle}>
        <td className="px-2 py-2 text-neutral-500">
          <button
            type="button"
            aria-expanded={open}
            aria-label={open ? 'Hide stack' : 'Show stack'}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            className="rounded p-0.5 hover:bg-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
          >
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        </td>
        <td className="px-2 py-2">
          <span
            className={cn(
              'inline-block rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ring-1',
              g.source === 'web'
                ? 'bg-blue-500/15 text-blue-300 ring-blue-500/30'
                : 'bg-amber-500/15 text-amber-300 ring-amber-500/30',
            )}
          >
            {g.source}
          </span>
        </td>
        <td className="max-w-0 px-2 py-2">
          <div className="truncate text-neutral-100" title={g.message}>
            {g.message}
          </div>
          <div className="truncate text-xs text-neutral-500">
            {g.sampleRoute ?? '—'}
            {g.sampleStatus ? ` · ${g.sampleStatus}` : ''}
            {g.resolvedAt ? ` · resolved ${formatDateTime(g.resolvedAt)}` : ''}
          </div>
        </td>
        <td className="px-2 py-2 text-right font-mono text-neutral-200">{g.occurrences.toLocaleString()}</td>
        <td className={`px-2 py-2 text-xs text-neutral-400 ${WIDE}`}>{formatDateTime(g.firstSeenAt)}</td>
        <td className={`px-2 py-2 text-xs text-neutral-400 ${WIDE}`}>{formatDateTime(g.lastSeenAt)}</td>
        <td className="px-2 py-2 text-right" onClick={(e) => e.stopPropagation()}>
          <button
            disabled={busy}
            onClick={() => onResolve(!g.resolvedAt)}
            className="inline-flex items-center gap-1 rounded-md bg-neutral-800 px-2 py-1 text-xs text-neutral-200 hover:bg-neutral-700 disabled:opacity-40"
          >
            {g.resolvedAt ? <RotateCcw size={12} /> : <CheckCircle2 size={12} />}
            {g.resolvedAt ? 'Reopen' : 'Resolve'}
          </button>
        </td>
      </tr>
      {open ? (
        <tr className="bg-neutral-950/60">
          <td></td>
          <td colSpan={6} className="px-2 py-3">
            <dl className="mb-2 grid grid-cols-[110px_1fr] gap-x-3 gap-y-1 text-xs">
              <dt className="text-neutral-500">Top frame</dt>
              <dd className="break-all font-mono text-neutral-300">{g.topFrame ?? '—'}</dd>
              <dt className="text-neutral-500">Release</dt>
              <dd className="font-mono text-neutral-300">{g.sampleRelease ?? '—'}</dd>
              <dt className="text-neutral-500">User agent</dt>
              <dd className="break-all text-neutral-300">{g.sampleUserAgent ?? '—'}</dd>
              <dt className="text-neutral-500">Last user id</dt>
              <dd className="font-mono text-neutral-300">
                {g.sampleUserId ? (
                  <Link to="/users/$id" params={{ id: g.sampleUserId }} className="hover:text-orange-300 hover:underline">
                    {g.sampleUserId}
                  </Link>
                ) : (
                  'anonymous'
                )}
              </dd>
            </dl>
            <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-all rounded-md bg-neutral-900 p-3 font-mono text-[11px] leading-relaxed text-neutral-300 ring-1 ring-neutral-800">
              {g.sampleStack ?? 'No stack captured.'}
            </pre>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function Segmented({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-1 rounded-md bg-neutral-900 px-2 py-1 text-xs ring-1 ring-neutral-800">
      <span className="mr-1 text-neutral-500">{label}:</span>
      {options.map((o) => (
        <button
          key={o}
          onClick={() => onChange(o)}
          className={cn(
            'rounded px-2 py-0.5',
            o === value ? 'bg-neutral-700 text-neutral-50' : 'text-neutral-400 hover:text-neutral-200',
          )}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}
